'use client';

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// `checkVisibility` is what catches a return-focus target hidden by a
// responsive ancestor: computed styles on the element itself still report its
// own `display` under a `display:none` parent. Environments without it (jsdom)
// fall through to restoring focus as before.
const isVisible = (element: HTMLElement) =>
  element.isConnected && (element.checkVisibility?.() ?? true);

const focusableWithin = (container: HTMLElement | null): HTMLElement[] =>
  container
    ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    : [];

/**
 * Modal keyboard contract for a `role="dialog"` overlay: focus moves into the
 * dialog when it opens, Tab cycles inside it, Escape closes it, and focus
 * returns to whatever was focused before (the trigger) when it closes. When
 * that trigger is no longer visible — a breakpoint change can hide the whole
 * mobile bar while the drawer is open — focus lands on the page's `main`
 * landmark instead of being dropped on the body. Either way the page keeps the
 * scroll position it had when the dialog opened.
 *
 * Collapsed accordion sections unmount their contents, so the focusable set is
 * re-read on every Tab rather than captured once on open.
 */
export function useDialogFocusTrap({
  isOpen,
  containerRef,
  onClose,
}: {
  isOpen: boolean;
  containerRef: RefObject<HTMLElement>;
  onClose: () => void;
}): void {
  // Kept in a ref so an inline `onClose` (a new function identity every render
  // of the parent) cannot re-run the effect, which would re-capture the
  // return-focus target from inside the open dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    focusableWithin(container)[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(containerRef.current);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const isOutside = !containerRef.current?.contains(active);

      if (event.shiftKey && (active === first || isOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || isOutside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // `preventScroll` because this runs while the scroll lock is still
      // applied: scrolling the return-focus target into view under a locked
      // body resolves to the top of the document, so a plain `focus()` sends
      // a reader who had scrolled into the results back to the page header.
      if (previouslyFocused && isVisible(previouslyFocused)) {
        previouslyFocused.focus?.({ preventScroll: true });
        return;
      }
      const main = document.getElementById('main');
      if (main) {
        main.tabIndex = -1;
        main.focus({ preventScroll: true });
      }
    };
  }, [isOpen, containerRef]);
}
